import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select, SelectItem } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { InlineError } from './states';

// Image store of Nội dung > Banner (he A banner folder): one upload path for banners, login popups and image events.
export type Images = { anh: { anh: string; nguon: string; bytes: number }[]; toiDa: number; soBannerToiDa: number };

// images are content-addressed (the name is a hash of the bytes), so one object URL per name
const urls = new Map<string, Promise<string>>();
export function Thumb({ anh, wide }: { anh: string; wide?: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    let live = true;
    setSrc(null); setBad(false);
    if (!urls.has(anh)) urls.set(anh, api.blobUrl('/api/admin/content/banners/' + encodeURIComponent(anh)).catch((e) => { urls.delete(anh); throw e; }));
    urls.get(anh)!.then((u) => { if (live) setSrc(u); }, () => { if (live) setBad(true); });
    return () => { live = false; };
  }, [anh]);
  const cls = 'thumb' + (wide ? ' thumb--wide' : '');
  if (bad) return <div className={cls + ' thumb--missing'}>Không có ảnh</div>;
  return src ? <img className={cls} src={src} alt={anh} /> : <div className={cls} aria-busy="true" />;
}

// Pick a stored image or upload a new one (PNG/JPG/WEBP); empty value = no image when `optional`.
export function ImageField({ id, value, onChange, optional }: { id: string; value?: string; onChange: (anh: string | undefined) => void; optional?: boolean }) {
  const qc = useQueryClient();
  const images = useQuery({ queryKey: ['banners'], queryFn: () => api.get<Images>('/api/admin/content/banners') });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const toiDa = images.data?.toiDa;
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new ApiError(400, 'KHONG_PHAI_ANH', 'Chỉ nhận ảnh PNG, JPG hoặc WEBP');
      if (toiDa && file.size > toiDa) throw new ApiError(400, 'ANH_QUA_LON', `Ảnh ${Math.ceil(file.size / 1024)} KB, tối đa ${Math.floor(toiDa / 1024)} KB`);
      return api.upload<{ anh: string; bytes: number }>('/api/admin/content/banners', file);
    },
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['banners'] }); onChange(r.anh); },
  });
  const names = (images.data?.anh ?? []).map((a) => a.anh);
  if (value && !names.includes(value)) names.push(value);
  return (
    <div className="stack">
      <div className="row row--top">
        <Select id={id} labelText="Ảnh" value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
          {optional ? <SelectItem value="" text="Không dùng ảnh" /> : value ? null : <SelectItem value="" text="Chọn ảnh" />}
          {names.map((a) => <SelectItem key={a} value={a} text={a} />)}
        </Select>
        <div>
          <label className="cds--btn cds--btn--tertiary cds--btn--sm" htmlFor={id + '-up'}>Tải ảnh lên</label>
          <input id={id + '-up'} className="visually-hidden" type="file" accept="image/png,image/jpeg,image/webp" disabled={upload.isPending}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload.mutate(f); }} />
        </div>
      </div>
      <InlineError error={upload.error} />
      {value ? <Thumb anh={value} /> : null}
    </div>
  );
}
